import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Input } from '../../components/Inputs/Input';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';
import { SignInStatus } from '../../components/WhatsNew/SignInStatus';
import { useLocalAuth } from '../../components/LocalAuthProvider/LocalAuthProvider';
import { isMcpOAuthAuthorizeReturn, mcpAuthorizeUrl } from '../OAuthConsent/oauth-urls';

import styles from './Login.module.css';

// Staging-only build stamp (short commit SHA) rendered under the form. Lets us
// confirm from a screenshot that a device is running the latest deployed
// bundle rather than a stale cached one. Hidden in production builds, which
// omit VITE_APP_ENV.
const BUILD_STAMP =
  import.meta.env.VITE_APP_ENV === 'staging' && import.meta.env.VITE_SENTRY_RELEASE
    ? String(import.meta.env.VITE_SENTRY_RELEASE).slice(0, 7)
    : null;

export default function Login() {
  const { login, isAuthenticated, isLoading } = useLocalAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/dashboard';
  const oauthReturn = isMcpOAuthAuthorizeReturn(searchParams);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && isAuthenticated && oauthReturn) {
      window.location.assign(mcpAuthorizeUrl(searchParams));
    }
  }, [isLoading, isAuthenticated, oauthReturn, searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await login(email, password);
      if (isMcpOAuthAuthorizeReturn(searchParams)) {
        window.location.assign(mcpAuthorizeUrl(searchParams));
        return;
      }
      navigate(returnTo);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <RevvlyMark className={styles.loginMark} aria-hidden="true" />
        <h1 className={styles.loginLogo}>Revvly</h1>
        <p className={styles.loginSubtitle}>Sign in to your account</p>

        {errorMessage && <div className={styles.loginError}>{errorMessage}</div>}

        <form className={styles.loginForm} onSubmit={handleSubmit}>
          <div className={styles.loginField}>
            <label className={styles.loginLabel} htmlFor="login-email">
              Email
            </label>
            <Input
              id="login-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className={styles.loginField}>
            <label className={styles.loginLabel} htmlFor="login-password">
              Password
            </label>
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className={styles.loginForgotRow}>
            <Link className={styles.loginForgot} to="/forgot-password">
              Forgot password?
            </Link>
          </div>

          <button className={styles.loginButton} type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <SignInStatus />

        {BUILD_STAMP && <p className={styles.loginBuild}>build {BUILD_STAMP}</p>}
      </div>
    </div>
  );
}
